<template>
  <div>
    <span class="column-action-button bg-light d-flex justify-content-center align-items-center border" :id="`comments-${id}-${_uid}`">
      <i class="mdi mdi-comment-text-outline"></i>
    </span>
    <b-popover
      :target="`comments-${id}-${_uid}`"
      triggers="hover focus"
      placement="right"
      boundary="viewport"
      :delay="{ show: 800, hide: 100 }"
      @show="loadComments"
      custom-class="datatables-comments-popover"
    >
      <template #title>
        <div class="px-2 d-flex align-items-center">
          <i class="mdi mdi-comment-text-outline mr-3" style="font-size: 1.5rem"></i>
          <span>Comentarios</span>
        </div>
      </template>
      <template v-if="!isLoading">
        <div class="scroll" v-if="!!comments.length" v-chat-scroll="{ smooth: true }">
          <b-card v-for="(comment, index) in comments" :key="comment._id" no-body>
            <b-card-body class="p-2 border">
              <div class="d-flex mb-4 justify-content-between">
                <div class="d-flex">
                  <b-avatar class="mr-3" :src="comment.createdBy.user.avatar" :text="avatarText(comment.createdBy.user)" variant="info"></b-avatar>
                  <div>
                    <h5 class="mt-0 mb-1">{{ fullName(comment.createdBy.user) }}</h5>
                    <b-card-text class="text-muted datetime-text">{{ dateTimeFormat(comment.createdAt) }}</b-card-text>
                  </div>
                </div>
                <b-dropdown
                  v-if="!comment.deleted"
                  right
                  text="Right align"
                  variant="link"
                  toggle-class="text-decoration-none shadow-none text-body p-0"
                  no-caret
                  boundary="viewport"
                >
                  <template #button-content>
                  <i class="mdi mdi-dots-vertical clickable" style="font-size: 22px"></i><span class="sr-only">Opciones</span>
                  </template>
                  <b-dropdown-item @click="onReplyComment(comment)">Responder</b-dropdown-item>
                  <b-dropdown-item
                    v-if="userCanDelete(comment.createdBy.user)"
                    variant="danger"
                    @click="onDeleteComment(comment, index)"
                  >
                    Eliminar
                  </b-dropdown-item>
                </b-dropdown>
              </div>
              <div v-html="comment.message" id="comments-message">
              </div>
            </b-card-body>
          </b-card>
          <div class="p-2 m-0 d-flex flex-column border-top">
            <LmEditor
              hideMenus
              useMentions
              scrollable
              :editorHeight="80"
              v-model="message"
              placeholder="Escribe tu mensaje aquí..."
              ref="lmEditor"
              :disabled="false"
              @mentions-list="onUsersMentioned"
            />
            <LmButton
              buttonClass="btn-primary"
              class="align-self-end mt-2"
              :value="$t('comment')"
              :disabled="false"
              @click="onAddComment"
            />
          </div>
        </div>
        <div v-else class="no-comments">
          <LmSvgIcon
            style="height: 175px"
            wrapperClass="d-flex justify-content-center align-items-center flex-column"
            width="6em"
            icon="LmNoDataIcon"
            text="No hay comentarios, ¿Por qué no escribes uno?"
          />
          <div class="p-2 m-0 d-flex flex-column border-top">
            <LmEditor
              hideMenus
              useMentions
              scrollable
              :editorHeight="80"
              v-model="message"
              placeholder="Escribe tu mensaje aquí..."
              ref="lmEditor"
              :disabled="false"
              @mentions-list="onUsersMentioned"
            />
            <LmButton
              buttonClass="btn-primary"
              class="align-self-end mt-2"
              :value="$t('comment')"
              :disabled="false"
              @click="onAddComment"
            />
          </div>
        </div>
      </template>
      <!-- loading state -->
      <template v-else>
        <b-card no-body>
            <b-card-body class="p-2 border">
              <div class="d-flex mb-4">
                <div class="d-flex">
                  <b-skeleton type="avatar" class="mr-3"></b-skeleton>
                  <div>
                    <b-skeleton width="80px"></b-skeleton>
                    <b-skeleton width="125px"></b-skeleton>
                  </div>
                </div>
              </div>
              <b-skeleton width="90%"></b-skeleton>
              <b-skeleton width="70%"></b-skeleton>
              <b-skeleton width="50%"></b-skeleton>
            </b-card-body>
        </b-card>
      </template>
    </b-popover>
  </div>
</template>

<script>
// Components
import LmEditor from '@/components/form/elements/lmEditor'
import LmButton from '@/components/form/elements/LmButton'

// Vuex
import { mapGetters } from 'vuex'

// Utils
import commonsApi from '@/api/commons'
import moment from 'moment'
import 'moment/locale/es'
moment.locale('es')

export default {
  props: {
    id: {
      type: String,
      required: true
    },
    source: {
      type: String,
      required: true
    }
  },
  components: {
    LmEditor,
    LmButton
  },
  data () {
    return {
      isLoading: false,
      comments: [],
      message: null,
      mentions: {
        click_action: this.$route.path,
        mentioned: []
      }
    }
  },
  computed: {
    ...mapGetters('session', ['userInfo'])
  },
  methods: {
    async loadComments () {
      try {
        this.isLoading = true
        const res = await commonsApi.axiosGetWrapper(`/comments/all/${this.source}/${this.id}`)
        this.comments = res.data
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    dateTimeFormat (datetime) {
      return moment(datetime).format('YYYY-MM-DD HH:mm')
    },
    avatarText (user) {
      return user.firstname.charAt(0) + user.lastname.charAt(0)
    },
    fullName (user) {
      return `${user.firstname} ${user.lastname}`
    },
    onUsersMentioned (event) {
      this.mentions.mentioned = event
    },
    userCanDelete (user) {
      return user.id === this.userInfo.id
    },
    async onAddComment () {
      try {
        if (this.message) {
          const dataToSendToServer = {
            message: this.message,
            linkId: this.id,
            linkType: this.source,
            lumbreMentions: this.mentions
          }
          const comment = await commonsApi.axiosPostWrapper('/comments', dataToSendToServer)
          this.comments.push(comment.data)
          this.message = null
          this.$emit('input', this.comments)
        }
      } catch (error) {
        console.log(error)
      }
    },
    onReplyComment (comment) {
      const quoteReply = `<blockquote>${comment.message}</blockquote>`
      const userReply = `<span data-type="mention" class="mention" data-id="${comment.createdBy.user.id}" data-label="${this.fullName(comment.createdBy.user)}" contenteditable="false">${this.fullName(comment.createdBy.user)}</span>&ensp;`
      this.message = quoteReply + userReply

      this.$nextTick(() => {
        this.$refs.lmEditor.editor.commands.focus('end')
      })
    },
    async onDeleteComment (comment, index) {
      try {
        await commonsApi.axiosPutWrapper(`/comments/delete/${comment._id}`)
        this.comments.at(index).deleted = true
        this.comments.at(index).message = '<p class="text-muted font-italic">Comentario eliminado.</p>'
      } catch (error) {
        console.log(error)
      }
    }
  }
}
</script>
